import React, { useEffect } from 'react';
import { Button, Col, Modal, Row } from 'antd';
import cx from 'classnames';
import { isNil } from 'lodash';
import moment from 'moment/moment';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  getActionDescription,
  getActionIcon,
  getActionTitle,
  getActionTitleModal,
} from 'utils/assignmentUtils';
import { AssignmentTerminationModel } from 'api/backofficeApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './AssignmentActionModal.module.less';

export enum AssignmentActionEnum {
  Edit = 1,
  Renew = 2,
  Terminate = 3,
  Rehire = 4,
}

type Props = {
  onClose: () => void;
  activeAction?: AssignmentActionEnum;
  handleActiveActionChange: (activeAction?: AssignmentActionEnum) => void;
  handleActionDetailsModalOpen: (value: boolean) => void;
  isLoading?: boolean;
  assignmentTerminationDetails?: AssignmentTerminationModel;
  isDisabledAssignmentEdit: boolean;
  isDisabledAssignmentRenew: boolean;
  isDisabledAssignmentTermination: boolean;
  isDisabledAssignmentRehire: boolean;
  confirmationTitleForRenewing?: string;
  confirmationTitleForRehiring?: string;
};

export default function AssignmentActionModal(props: Props) {
  const {
    onClose,
    activeAction,
    handleActiveActionChange,
    handleActionDetailsModalOpen,
    isLoading,
    assignmentTerminationDetails,
    isDisabledAssignmentEdit,
    isDisabledAssignmentRenew,
    isDisabledAssignmentTermination,
    isDisabledAssignmentRehire,
    confirmationTitleForRenewing,
    confirmationTitleForRehiring,
  } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const hasTerminationDetails = !isNil(assignmentTerminationDetails);
  const isRenderRenew = !isAdminRole;
  const isRenderRehire = !isAdminRole;

  const handleAssignmentAction = (activeAction?: AssignmentActionEnum) => {
    if (
      activeAction === AssignmentActionEnum.Renew &&
      confirmationTitleForRenewing
    ) {
      Modal.confirm({
        title: confirmationTitleForRenewing,
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          handleActiveActionChange(activeAction);
        },
        onCancel() {},
      });
    } else if (
      activeAction === AssignmentActionEnum.Rehire &&
      confirmationTitleForRehiring
    ) {
      Modal.confirm({
        title: confirmationTitleForRehiring,
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          handleActiveActionChange(activeAction);
        },
        onCancel() {},
      });
    } else {
      handleActiveActionChange(activeAction);
    }
  };

  useEffect(() => {
    if (!isDisabledAssignmentEdit)
      return handleAssignmentAction(AssignmentActionEnum.Edit);
    if (!isDisabledAssignmentRenew)
      return handleAssignmentAction(AssignmentActionEnum.Renew);
    if (!isDisabledAssignmentTermination)
      return handleAssignmentAction(AssignmentActionEnum.Terminate);
    if (!isDisabledAssignmentRehire)
      return handleAssignmentAction(AssignmentActionEnum.Rehire);

    handleAssignmentAction(undefined);
    // eslint-disable-next-line
  }, [
    isDisabledAssignmentEdit,
    isDisabledAssignmentRenew,
    isDisabledAssignmentTermination,
    isDisabledAssignmentRehire,
  ]);

  const isActionDisabled = (actionType: AssignmentActionEnum): boolean => {
    switch (actionType) {
      case AssignmentActionEnum.Terminate:
        return isDisabledAssignmentTermination;
      case AssignmentActionEnum.Renew:
        return isDisabledAssignmentRenew;
      case AssignmentActionEnum.Rehire:
        return isDisabledAssignmentRehire;
      case AssignmentActionEnum.Edit:
        return isDisabledAssignmentEdit;
      default:
        return false;
    }
  };

  const renderAssignmentActionTab = (actionType: AssignmentActionEnum) => {
    const isDisabled = isActionDisabled(actionType);

    return (
      <>
        <div
          className={cx(styles.card, {
            [styles.active]: actionType === activeAction,
            [styles.disabled]: isDisabled,
          })}
        >
          <Button
            disabled={isDisabled}
            onClick={() => handleAssignmentAction(actionType)}
          >
            <div className={styles.cardBtn}>
              <i className={getActionIcon(actionType)} />
            </div>
            <div className={styles.cardText}>
              {getActionTitle(actionType)}

              {actionType === AssignmentActionEnum.Terminate &&
                hasTerminationDetails && (
                  <div>
                    Scheduled for{' '}
                    {moment(
                      assignmentTerminationDetails?.terminationDate,
                    ).format('MM/DD/YYYY')}
                  </div>
                )}
            </div>
          </Button>
        </div>

        <div
          className={cx(styles.description, {
            [styles.active]: actionType === activeAction,
            [styles.disabled]: isDisabled,
          })}
        >
          {getActionDescription(actionType)}
        </div>
      </>
    );
  };

  const handleNextClick = () => {
    handleActionDetailsModalOpen(true);
  };

  return (
    <BaseModal
      title={getActionTitleModal(activeAction)}
      open={true}
      onCancel={onClose}
      className={styles.modal}
    >
      <div className="modal-scroll-wrapper">
        <div className="modal-capture">Please select an assignment action.</div>

        <Row gutter={[20, 20]}>
          <Col xs={24} md={isRenderRenew ? 6 : 12}>
            {renderAssignmentActionTab(AssignmentActionEnum.Edit)}
          </Col>

          {isRenderRenew && (
            <Col xs={24} md={6}>
              {renderAssignmentActionTab(AssignmentActionEnum.Renew)}
            </Col>
          )}

          <Col xs={24} md={isRenderRenew ? 6 : 12}>
            {renderAssignmentActionTab(AssignmentActionEnum.Terminate)}
          </Col>

          {isRenderRehire && (
            <Col xs={24} md={6}>
              {renderAssignmentActionTab(AssignmentActionEnum.Rehire)}
            </Col>
          )}
        </Row>
      </div>

      <BaseModalFooter>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleNextClick}
          loading={isLoading}
          type="primary"
          className="with-icon"
          disabled={isNil(activeAction)}
        >
          Next <span className="icon icon-double-right" />
        </Button>
      </BaseModalFooter>
    </BaseModal>
  );
}
